/* eslint-disable react/jsx-props-no-spreading */
import { Box, FormControlLabel, Tooltip, Typography } from '@mui/material';
import UPDatepicker from 'components/datepicker';
import UPFileInput from 'components/file-input';
import UPInput from 'components/input';
import UPSwitch from 'components/switch';
import { toNumber } from 'lodash';
import { Document } from 'modules/documents/types';
import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { downloadIdAction } from 'store/actions/employee-actions';
import {
  employeeSelector,
  isOnboardingExistingEmployeeSelector,
  onboardingSelector,
} from 'store/selectors/employee-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import theme from 'theme';
import { AgencySettingKey } from 'services/agency-settings/types';
import { agencySettingsStore } from 'services/agency-settings';
import { UPDropdownItem } from 'components/dropdown';
import UPDropzone from 'components/dropzone/UPDropzone';
import UPMfaOverrideDropdown from 'components/mfa-override-dropdown';
import UPMfaResetAction from 'components/mfa-reset';
import useCanWriteMfaOverride from 'hooks/useCanWriteMfaOverride';
import PasswordStrength from 'components/password-strength/PasswordStrength';
import { CompanyRole } from 'constants/CompanyRole';
import { EmployeeExistsResponse } from 'constants/EmployeeExistsResponse';
import { EMPLOYEE__ACADEMIC_YEAR_END, EMPLOYEE__ACADEMIC_YEAR_START } from 'store/constants';
import {
  DOCUMENTS__BROWSE,
  DOCUMENTS__DRAG_AND_DROP_STUD_DECLARATION,
  DOCUMENTS__PERIOD_END,
  DOCUMENTS__PERIOD_START,
  DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED,
  EMPLOYEE__FIND_LETTER_OF_INTENT_TEXT,
  EMPLOYEE__FIND_STUDENT_DECLARATION_TEXT,
  EMPLOYEE__INVALID_ACADEMIC_PERIOD,
  EMPLOYEE__LETTER_OF_INTENT,
  EMPLOYEE__STUDENT_DECLARATION,
  EMPLOYEE__UPLOADED_DOCUMENT,
  EMPLOYEE__WORKERCLASS_NOT_SELECTED_ERROR,
  EMPLOYEES__ACCOUNT,
  EMPLOYEES__AGREE_TO,
  EMPLOYEES__AGREE_TO_GDPR,
  EMPLOYEES__AGREE_TO_GDPR_ERROR,
  EMPLOYEES__CHECK_GDPR_LINK,
  EMPLOYEES__CONFIRM_PASSWORD,
  EMPLOYEES__ID_CARD_NUMBER,
  EMPLOYEES__LINK_EXISTING_ACCOUNT,
  EMPLOYEES__PICTURE_AGREEMENT_LINK,
  EMPLOYEES__UPLOAD_ID_BACK,
  EMPLOYEES__UPLOAD_ID_FRONT,
  EMPLOYEES__UPLOAD_PICTURE,
  EMPLOYEES__USE_EXISTING_PASSWORD,
  EMPLOYEES__VIEW_ID,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD_PLACEHOLDER,
  GENERAL__AND,
  GENERAL__HERE,
  GENERAL__SECURITY,
  LOGIN__EXISTING_PASSWORD_LABEL,
  LOGIN__PASSWORD_LABEL,
  LOGIN__PASSWORD_MIN_LENGTH_ERROR,
} from 'translations/constants';
import Lang from 'types/Lang';
import { getInputErrorText, getUpDropdownItemsWithIdAsValueFromEnum, validatePassword } from 'utils/helpers';
import { shouldSendMfaOverride } from 'utils/mfa-override';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { getDaysBetweenDates, isIdPeriodValid } from '../helpers';
import EligibilityAndTerminationDetails from './EligibilityAndTerminationDetails';
import {
  AccountSmallBox,
  AccountWithMarginLeftBox,
  AccountWithMarginRightBox,
  EmployeeExistsText,
  EmployeeInfoSmallBox,
  EmployeeModalInfoContainer,
  EmployeeModalPasswordContainer,
  ErrorText,
  InfoText,
  PictureAgreementLink,
  RowContainer,
  SectionTitle,
  SmallBoxWithNoMargin,
  SecuritySubsectionContainer,
  SubsectionInputContainer,
  SubsectionTitle,
  SubsectionTitleContainer,
} from './styles';
import { IAccountTabProps } from './types';

const AccountTab = (props: IAccountTabProps) => {
  const {
    control,
    errors,
    setValue,
    getValues,
    setError,
    clearErrors,
    employeeIdURL,
    employeeIdBackURL,
    companyId,
    watch,
    proofOfInscription,
    setProofOfInscription,
    setNewDocToAdd,
    setDocToUpdate,
    isEdit,
    employeeExists,
  } = props;

  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const [canChangeIdRelatedFields, setCanChangeIdRelatedFields] = useState<boolean>(false);

  const employeeOnboarding = useAppSelector(onboardingSelector);
  const profile = useAppSelector(profileSelector);
  const isAdmin = profile?.role === CompanyRole.ADMIN;
  const canWriteMfaOverride = useCanWriteMfaOverride();
  const employee = useAppSelector(employeeSelector);
  const userLanguageId = useAppSelector(profileSelector)?.languageId;
  const isExistingEmployeeOnboarding = useAppSelector(isOnboardingExistingEmployeeSelector);

  const watchPermanent = watch('permanent');
  const watchWorkerClass = watch('workerclass');
  const watchLetterOfIntentCheck = watch('letterOfIntentCheck');
  const watchIdUpdate = watch('idUpload');
  const watchIdBackUpdate = watch('idUploadBack');
  const watchPassword = watch('password');
  const watchPictureUploadURL = watch('pictureUploadURL');

  const isManagerEdit =
    ['Company manager', 'Store/location manager', 'HR manager'].includes(profile?.role) && employee?.id > 0;
  const canSeeEligibilityAndTerminationDetails = (isAdmin || isManagerEdit) && isEdit;

  useEffect(() => {
    if (watchPictureUploadURL === '') {
      setValue('agreePictureAgreement', false);
    }
  }, [watchPictureUploadURL, setValue]);

  useEffect(() => {
    if (!isEdit || (isEdit && watchIdUpdate !== null && watchIdBackUpdate !== null)) {
      setCanChangeIdRelatedFields(true);
    }
  }, [isEdit, watchIdUpdate, watchIdBackUpdate]);

  const [agencySettings, setAgencySettings] = useState(agencySettingsStore.get());

  useEffect(() => {
    const loadSettings = async () => {
      const settings = await agencySettingsStore.getOrLoad();
      setAgencySettings(settings);
    };

    if (!agencySettings) {
      loadSettings();
    }
  }, [agencySettings]);

  const privacyPolicyLink = agencySettings?.[AgencySettingKey.PrivacyPolicyLink] ?? '';

  function getBase64(file: any) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setValue('pictureBase64', reader.result);
    };
  }

  function formatAndSaveValue(value: any, input: string): string {
    // check if value.target.files[0] is undefined or has no value
    const file = value?.target?.files?.[0];
    if (!file) {
      // Clear the file when no file is selected (e.g., on clear)
      if (input === 'id-upload') {
        setValue('idUpload', null);
        clearErrors('idUploadURL');
      } else if (input === 'id-upload-back') {
        setValue('idUploadBack', null);
        clearErrors('idUploadBackURL');
      } else if (input === 'picture-upload') {
        setValue('pictureUpload', null);
        setValue('pictureBase64', null);
        clearErrors('pictureUploadURL');
      }
      return '';
    }

    if (input === 'id-upload') {
      setValue('idUpload', file);
      clearErrors('idUploadURL');
    } else if (input === 'id-upload-back') {
      setValue('idUploadBack', file);
      clearErrors('idUploadBackURL');
    } else if (input === 'picture-upload') {
      setValue('pictureUpload', file);
      clearErrors('pictureUploadURL');
      getBase64(file);
    }

    return file?.name;
  }

  const isAcademicPeriodValid = (startDate: Date, endDate: Date) => {
    if (!endDate && startDate) {
      return true;
    }

    const difference = getDaysBetweenDates(startDate, endDate);
    const numberOfDaysInAYear = 365;
    if (difference < 0 || difference > numberOfDaysInAYear) {
      return false;
    }
    return true;
  };

  const onDropFile = (file: any) => {
    if (file) {
      const newDocument: Document = {
        file,
        id: 1,
        name: (file as any).path || file.name,
        type: 6,
        error: {
          dateError: false,
          typeError: false,
          invalidDate: false,
        },
      };
      setNewDocToAdd(true);
      setProofOfInscription(newDocument);
    }
  };

  const onDownloadImage = (image: string) => {
    if (image) {
      dispatch(
        downloadIdAction({
          companyId: toNumber(companyId),
          employeeId: employee.id,
          imageName: image,
        }),
      );
    }
  };

  const languageOptions: UPDropdownItem[] = useMemo(() => {
    return getUpDropdownItemsWithIdAsValueFromEnum(Lang);
  }, []);
  const userLanguageLabel = languageOptions.find(l => l.value === userLanguageId)?.label;
  const pictureAgreementUrls: Record<string, string> = {
    nl: 'https://updateproproduction.blob.core.windows.net/common/picture_agreement/Picture agreement NL.pdf',
    fr: 'https://updateproproduction.blob.core.windows.net/common/picture_agreement/Picture agreement FR.pdf',
    en: 'https://updateproproduction.blob.core.windows.net/common/picture_agreement/Picture agreement ENG.pdf',
  };
  const pictureAgreementUrl = pictureAgreementUrls[userLanguageLabel] || pictureAgreementUrls.nl;

  return (
    <Box>
      <RowContainer>
        <SectionTitle>{t(EMPLOYEES__ACCOUNT)}</SectionTitle>
      </RowContainer>
      <EmployeeModalInfoContainer>
        <EmployeeInfoSmallBox sx={{ width: '20%' }}>
          <Controller
            name="idUploadURL"
            control={control}
            rules={{
              required: !watchPermanent,
              validate: value => {
                if (!value) return true;
                const idUploadFile = getValues('idUpload');
                if (idUploadFile) {
                  const fileExtension = idUploadFile.name.split('.').pop()?.toLowerCase();
                  const rawExtensions = ['dng', 'cr2', 'nef', 'orf', 'rw2', 'crw'];
                  if (fileExtension && rawExtensions.includes(fileExtension)) {
                    return `${t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED)}`;
                  }
                }
                return true;
              },
            }}
            render={({ field: { value, onChange } }) => (
              <UPFileInput
                value={value}
                onChange={ev => {
                  const newValue = formatAndSaveValue(ev, 'id-upload');
                  onChange(newValue);
                }}
                onValidationError={error => {
                  setValue('idUploadURL', '');
                }}
                label={t(EMPLOYEES__UPLOAD_ID_FRONT)}
                inputName="idUpload"
                placeholder={t(EMPLOYEES__UPLOAD_ID_FRONT)}
                error={!!errors.idUploadURL}
                required={!watchPermanent}
                helperText={getInputErrorText(errors, 'idUploadURL')}
              />
            )}
          />
          {employeeIdURL && isAdmin && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <a
              onClick={() => onDownloadImage(employeeIdURL)}
              style={{
                textDecoration: 'none',
                fontSize: '12px',
                color: '#008C15',
                marginLeft: '5px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              {t(EMPLOYEES__VIEW_ID)}
            </a>
          )}
        </EmployeeInfoSmallBox>
        <EmployeeInfoSmallBox sx={{ width: '20%' }}>
          <Controller
            name="idUploadBackURL"
            control={control}
            rules={{
              required: !watchPermanent,
              validate: value => {
                if (!value) return true;
                const idUploadBackFile = getValues('idUploadBack');
                if (idUploadBackFile) {
                  const fileExtension = idUploadBackFile.name.split('.').pop()?.toLowerCase();
                  const rawExtensions = ['dng', 'cr2', 'nef', 'orf', 'rw2', 'crw'];
                  if (fileExtension && rawExtensions.includes(fileExtension)) {
                    return `${t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED)}`;
                  }
                }
                return true;
              },
            }}
            render={({ field: { value, onChange } }) => (
              <UPFileInput
                value={value}
                onChange={ev => {
                  const newValue = formatAndSaveValue(ev, 'id-upload-back');
                  onChange(newValue);
                }}
                onValidationError={error => {
                  setValue('idUploadBackURL', '');
                }}
                label={t(EMPLOYEES__UPLOAD_ID_BACK)}
                inputName="idUploadBack"
                placeholder={t(EMPLOYEES__UPLOAD_ID_BACK)}
                error={!!errors.idUploadBackURL}
                required={!watchPermanent}
                helperText={getInputErrorText(errors, 'idUploadBackURL')}
              />
            )}
          />
          {employeeIdBackURL && isAdmin && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <a
              onClick={() => onDownloadImage(employeeIdBackURL)}
              style={{
                textDecoration: 'none',
                fontSize: '12px',
                color: '#008C15',
                marginLeft: '5px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              {t(EMPLOYEES__VIEW_ID)}
            </a>
          )}
        </EmployeeInfoSmallBox>

        <EmployeeInfoSmallBox sx={{ width: '20%' }}>
          <Controller
            name="idCardNumber"
            control={control}
            rules={{ required: !watchPermanent, maxLength: 50 }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                onChange={field.onChange}
                label={t(EMPLOYEES__ID_CARD_NUMBER)}
                placeholder={t(EMPLOYEES__ID_CARD_NUMBER)}
                required={!watchPermanent}
                error={!!errors.idCardNumber}
                helperText={getInputErrorText(errors, 'idCardNumber', undefined, 50)}
                inputClassName="white-background"
                disabled={!canChangeIdRelatedFields}
              />
            )}
          />
        </EmployeeInfoSmallBox>
        <EmployeeInfoSmallBox sx={{ width: '20%' }}>
          <Controller
            name="idPeriodStart"
            control={control}
            rules={{ required: !watchPermanent }}
            render={({ field }) => (
              <UPDatepicker
                value={field.value}
                onChange={value => {
                  field.onChange(value);
                  const { idPeriodEnd } = getValues();
                  if (!isIdPeriodValid(value, idPeriodEnd, false)) {
                    setError('idPeriodInvalid', { type: 'idPeriodInvalid' });
                  } else {
                    clearErrors('idPeriodInvalid');
                  }
                }}
                label={t(DOCUMENTS__PERIOD_START)}
                required={!watchPermanent}
                error={!!errors.idPeriodStart}
                helperText={getInputErrorText(errors, 'idPeriodStart', undefined, 50)}
                className="datepicker--white-background"
                disabled={!canChangeIdRelatedFields}
              />
            )}
          />
        </EmployeeInfoSmallBox>
        <SmallBoxWithNoMargin sx={{ width: '20%' }}>
          <Controller
            name="idPeriodEnd"
            control={control}
            rules={{ required: !watchPermanent }}
            render={({ field }) => (
              <UPDatepicker
                value={field.value}
                onChange={value => {
                  field.onChange(value);
                  const { idPeriodStart } = getValues();
                  if (!isIdPeriodValid(idPeriodStart, value, false)) {
                    setError('idPeriodInvalid', { type: 'idPeriodInvalid' });
                  } else {
                    clearErrors('idPeriodInvalid');
                  }
                }}
                label={t(DOCUMENTS__PERIOD_END)}
                required={!watchPermanent}
                error={!!errors.idPeriodEnd || !!errors.idPeriodInvalid}
                helperText={getInputErrorText(errors, 'idPeriodEnd', undefined)}
                className="datepicker--white-background"
                disabled={!canChangeIdRelatedFields}
              />
            )}
          />
        </SmallBoxWithNoMargin>
      </EmployeeModalInfoContainer>
      {!employeeOnboarding && (
        <>
          {employeeExists === EmployeeExistsResponse.EXISTS_MATCH && (
            <Box mt={2}>
              <EmployeeExistsText>
                {isAdmin ? t(EMPLOYEES__LINK_EXISTING_ACCOUNT) : t(EMPLOYEES__USE_EXISTING_PASSWORD)}
              </EmployeeExistsText>
            </Box>
          )}
          <EmployeeModalInfoContainer sx={{ marginTop: '16px' }}>
            <AccountWithMarginRightBox>
              <Controller
                name="pictureUploadURL"
                control={control}
                rules={{
                  validate: value => {
                    if (!value) return true;
                    const pictureUploadFile = getValues('pictureUpload');
                    if (pictureUploadFile) {
                      const fileExtension = pictureUploadFile.name.split('.').pop()?.toLowerCase();
                      const rawExtensions = ['dng', 'cr2', 'nef', 'orf', 'rw2', 'crw'];
                      if (fileExtension && rawExtensions.includes(fileExtension)) {
                        return 'RAW image formats (DNG, CR2, NEF, ORF, RW2, CRW) are not supported. Please use JPEG, PNG, or other standard formats.';
                      }
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <UPFileInput
                    value={value}
                    onChange={ev => {
                      const newValue = formatAndSaveValue(ev, 'picture-upload');
                      onChange(newValue);
                    }}
                    onValidationError={error => {
                      setValue('pictureUploadURL', '');
                    }}
                    label={t(EMPLOYEES__UPLOAD_PICTURE)}
                    inputName="pictureUpload"
                    placeholder={t(EMPLOYEES__UPLOAD_PICTURE)}
                    error={!!errors.pictureUploadURL}
                    helperText={getInputErrorText(errors, 'pictureUploadURL', undefined)}
                    clearable
                  />
                )}
              />
            </AccountWithMarginRightBox>
            <AccountSmallBox display="flex" flexDirection="column" gap="8px">
              {!(isAdmin && employeeExists === EmployeeExistsResponse.EXISTS_MATCH) && (
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: !watchPermanent,
                    minLength: { value: 8, message: t(LOGIN__PASSWORD_MIN_LENGTH_ERROR) },
                    maxLength: 100,
                  }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={e => {
                        field.onChange(e);
                        if (!validatePassword(e.target.value)) {
                          setError('password', { type: 'adminEmployeePolicy' });
                        } else {
                          clearErrors('password');
                        }
                      }}
                      label={
                        employeeExists === EmployeeExistsResponse.EXISTS_MATCH
                          ? t(LOGIN__EXISTING_PASSWORD_LABEL)
                          : t(LOGIN__PASSWORD_LABEL)
                      }
                      placeholder={t(LOGIN__PASSWORD_LABEL)}
                      required={!watchPermanent}
                      type="password"
                      error={!!errors.password}
                      autoComplete="new-password"
                    />
                  )}
                />
              )}
              {employeeExists === EmployeeExistsResponse.DOES_NOT_EXIST && (
                <Box width="100%" height={30}>
                  <PasswordStrength password={watchPassword} />
                </Box>
              )}
            </AccountSmallBox>
          </EmployeeModalInfoContainer>
        </>
      )}
      {employeeOnboarding && (
        <>
          {employeeExists === EmployeeExistsResponse.EXISTS_MATCH && !isExistingEmployeeOnboarding && (
            <Box mt={4}>
              <InfoText>{t(EMPLOYEES__USE_EXISTING_PASSWORD)}</InfoText>
            </Box>
          )}
          <EmployeeModalPasswordContainer>
            <AccountWithMarginRightBox>
              <Controller
                name="pictureUploadURL"
                control={control}
                rules={{
                  validate: value => {
                    if (!value) return true;
                    const pictureUploadFile = getValues('pictureUpload');
                    if (pictureUploadFile) {
                      const fileExtension = pictureUploadFile.name.split('.').pop()?.toLowerCase();
                      const rawExtensions = ['dng', 'cr2', 'nef', 'orf', 'rw2', 'crw'];
                      if (fileExtension && rawExtensions.includes(fileExtension)) {
                        return 'RAW image formats (DNG, CR2, NEF, ORF, RW2, CRW) are not supported. Please use JPEG, PNG, or other standard formats.';
                      }
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <UPFileInput
                    value={value}
                    onChange={ev => {
                      const newValue = formatAndSaveValue(ev, 'picture-upload');
                      onChange(newValue);
                    }}
                    onValidationError={error => {
                      setValue('pictureUploadURL', '');
                    }}
                    label={t(EMPLOYEES__UPLOAD_PICTURE)}
                    inputName="pictureUpload"
                    placeholder={t(EMPLOYEES__UPLOAD_PICTURE)}
                    error={!!errors.pictureUploadURL}
                    helperText={getInputErrorText(errors, 'pictureUploadURL', undefined)}
                  />
                )}
              />
            </AccountWithMarginRightBox>
            {(isExistingEmployeeOnboarding || employeeExists === EmployeeExistsResponse.EXISTS_MATCH) && (
              <AccountSmallBox marginBottom={errors?.password ? 6 : 4}>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      label={t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD)}
                      placeholder={t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD_PLACEHOLDER)}
                      required
                      type="password"
                      error={!!errors.password}
                      helperText={getInputErrorText(errors, 'password', undefined) || errors.password?.message}
                      inputClassName="white-background"
                      autoComplete="new-password"
                    />
                  )}
                />
              </AccountSmallBox>
            )}
            {!isExistingEmployeeOnboarding && employeeExists !== EmployeeExistsResponse.EXISTS_MATCH && (
              <>
                <AccountSmallBox>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field }) => (
                      <UPInput
                        value={field.value}
                        onChange={event => {
                          const { confirmPassword } = getValues();
                          field.onChange(event.target.value);
                          if (confirmPassword !== event.target.value) {
                            setError('notMatchingPassword', { type: 'match' });
                          } else if (!validatePassword(event.target.value)) {
                            setError('notMatchingPassword', { type: 'policy' });
                          } else {
                            clearErrors('notMatchingPassword');
                          }
                        }}
                        label={isExistingEmployeeOnboarding ? 'Existing password' : t(LOGIN__PASSWORD_LABEL)}
                        placeholder={t(LOGIN__PASSWORD_LABEL)}
                        required
                        type="password"
                        error={!!errors.password}
                        helperText={getInputErrorText(errors, 'password', undefined, 100, false, 8, false, true)}
                        inputClassName="white-background"
                        autoComplete="new-password"
                      />
                    )}
                  />
                </AccountSmallBox>
                <AccountWithMarginLeftBox>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{
                      required: true,
                      minLength: { value: 8, message: t(LOGIN__PASSWORD_MIN_LENGTH_ERROR) },
                      maxLength: 200,
                    }}
                    render={({ field }) => (
                      <UPInput
                        value={field.value}
                        onChange={event => {
                          const { password } = getValues();
                          field.onChange(event.target.value);
                          if (password !== event.target.value) {
                            setError('notMatchingPassword', { type: 'match' });
                          } else if (!validatePassword(event.target.value)) {
                            setError('notMatchingPassword', { type: 'policy' });
                          } else {
                            clearErrors('notMatchingPassword');
                          }
                        }}
                        label={t(EMPLOYEES__CONFIRM_PASSWORD)}
                        placeholder={t(LOGIN__PASSWORD_LABEL)}
                        required
                        type="password"
                        error={!!errors.notMatchingPassword}
                        helperText={getInputErrorText(
                          errors,
                          'notMatchingPassword',
                          undefined,
                          100,
                          false,
                          8,
                          false,
                          true,
                        )}
                        inputClassName="white-background"
                        autoComplete="new-password"
                      />
                    )}
                  />
                </AccountWithMarginLeftBox>
              </>
            )}
          </EmployeeModalPasswordContainer>
        </>
      )}
      {watchPictureUploadURL && (
        <EmployeeModalInfoContainer sx={{ marginTop: '0px' }}>
          <Box sx={{ marginLeft: '16px' }}>
            <Controller
              name="agreePictureAgreement"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControlLabel
                  control={<UPSwitch checked={field.value} onChange={field.onChange} />}
                  label={
                    <span>
                      {`${t(EMPLOYEES__AGREE_TO)} `}
                      <PictureAgreementLink href={pictureAgreementUrl} target="blank">
                        {t(EMPLOYEES__PICTURE_AGREEMENT_LINK)}
                      </PictureAgreementLink>
                    </span>
                  }
                  sx={{
                    '.MuiTypography-root': {
                      fontSize: '14px',
                      marginLeft: '10px',
                    },
                    '&.MuiFormControlLabel-root': {
                      marginTop: '8px',
                      marginRight: '0px',
                    },
                  }}
                />
              )}
            />
          </Box>
        </EmployeeModalInfoContainer>
      )}
      {!!errors.agreePictureAgreement && watchPictureUploadURL && (
        <RowContainer sx={{ marginTop: '5px', marginLeft: '7px' }}>
          <ErrorText>{t(EMPLOYEES__AGREE_TO_GDPR_ERROR)}</ErrorText>
        </RowContainer>
      )}
      <EmployeeModalInfoContainer sx={{ marginTop: '0px' }}>
        <Box sx={{ marginLeft: '16px' }}>
          <Controller
            name="agreeGdpr"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControlLabel
                control={<UPSwitch checked={field.value} onChange={field.onChange} />}
                label={
                  <Tooltip
                    title={
                      <>
                        {`${t(EMPLOYEES__CHECK_GDPR_LINK)} `}
                        <a
                          href={privacyPolicyLink}
                          target="blank"
                          style={{
                            fontStyle: 'italic',
                            textDecoration: 'underline',
                            color: '#DBEDE3',
                          }}
                        >
                          {t(GENERAL__HERE)}
                        </a>
                      </>
                    }
                  >
                    <span>{t(EMPLOYEES__AGREE_TO_GDPR)}</span>
                  </Tooltip>
                }
                sx={{
                  '.MuiTypography-root': {
                    fontSize: '14px',
                    marginLeft: '10px',
                  },
                  '&.MuiFormControlLabel-root': {
                    marginTop: '8px',
                    marginRight: '0px',
                  },
                }}
              />
            )}
          />
        </Box>
      </EmployeeModalInfoContainer>
      {!!errors.agreeGdpr && (
        <RowContainer sx={{ marginTop: '5px', marginLeft: '7px' }}>
          <ErrorText>{t(EMPLOYEES__AGREE_TO_GDPR_ERROR)}</ErrorText>
        </RowContainer>
      )}
      {!watchPermanent && (
        <EmployeeModalInfoContainer sx={{ marginTop: '0px' }}>
          <Box sx={{ marginLeft: '16px', marginBottom: '10px' }}>
            <Controller
              name="letterOfIntentCheck"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControlLabel
                  control={<UPSwitch checked={field.value} onChange={field.onChange} disabled={!watchWorkerClass} />}
                  label={
                    <Tooltip
                      title={
                        <>
                          {watchWorkerClass === 1
                            ? t(EMPLOYEE__FIND_STUDENT_DECLARATION_TEXT)
                            : t(EMPLOYEE__FIND_LETTER_OF_INTENT_TEXT)}
                          <a
                            href={
                              watchWorkerClass === 1
                                ? 'https://updateproproduction.blob.core.windows.net/common/student_declarations/Employee%20approval_Intentieverklaring%202022-2023%20student.pdf'
                                : 'https://updateproproduction.blob.core.windows.net/common/letter_of_intent/Employee%20approval_Intentieverklaring.pdf'
                            }
                            target="blank"
                            style={{
                              fontStyle: 'italic',
                              textDecoration: 'underline',
                              color: '#DBEDE3',
                            }}
                          >
                            {' NED '}
                          </a>
                          {t(GENERAL__AND)}
                          <a
                            href={
                              watchWorkerClass === 1
                                ? 'https://updateproproduction.blob.core.windows.net/common/student_declarations/Employee%20approval_Intention%20de%20Conclure%20Etudiant%202022-2023.pdf'
                                : 'https://updateproproduction.blob.core.windows.net/common/letter_of_intent/Employee%20approval_Intention%20de%20Conclure.pdf'
                            }
                            target="blank"
                            style={{
                              fontStyle: 'italic',
                              textDecoration: 'underline',
                              color: '#DBEDE3',
                            }}
                          >
                            {' FR '}
                          </a>
                        </>
                      }
                      placement="top"
                    >
                      <span>{`${t(EMPLOYEE__LETTER_OF_INTENT)} / ${t(EMPLOYEE__STUDENT_DECLARATION)}`}</span>
                    </Tooltip>
                  }
                  sx={{
                    '.MuiTypography-root': {
                      fontSize: '14px',
                      marginLeft: '10px',
                    },
                    '&.MuiFormControlLabel-root': {
                      marginTop: '8px',
                      marginRight: '0px',
                    },
                  }}
                />
              )}
            />
          </Box>
        </EmployeeModalInfoContainer>
      )}
      {!!errors.letterOfIntentCheck && (
        <RowContainer sx={{ marginTop: '0px', marginLeft: '7px' }}>
          <ErrorText>{t(EMPLOYEES__AGREE_TO_GDPR_ERROR)}</ErrorText>
        </RowContainer>
      )}
      {!watchWorkerClass && !watchPermanent && (
        <RowContainer sx={{ marginTop: '5px', marginLeft: '10px' }}>
          <ErrorText>{t(EMPLOYEE__WORKERCLASS_NOT_SELECTED_ERROR)}</ErrorText>
        </RowContainer>
      )}
      {watchWorkerClass === 1 && watchLetterOfIntentCheck && (
        <>
          {!proofOfInscription ? (
            <RowContainer
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '10px 20px',
              }}
            >
              <UPDropzone
                onChange={onDropFile}
                onValidationError={() => {
                  setError('proofOfInscriptionFile', {
                    type: 'validation',
                    message: t(DOCUMENTS__UPLOAD_FORMAT_NOT_SUPPORTED),
                  });
                }}
                instructionText={t(DOCUMENTS__DRAG_AND_DROP_STUD_DECLARATION)}
                buttonText={t(DOCUMENTS__BROWSE)}
                noDrag={!!proofOfInscription}
                disabled={!!proofOfInscription}
              />
            </RowContainer>
          ) : (
            <Typography>{t(EMPLOYEE__UPLOADED_DOCUMENT)}: </Typography>
          )}
          <RowContainer
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '10px 32px',
              marginTop: '0px',
              maxHeight: '100px',
              overflowY: 'auto',
            }}
          >
            {proofOfInscription && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ marginRight: '16px', alignSelf: 'center' }} width={3 / 4}>
                    <div>{proofOfInscription.name}</div>
                  </Box>
                </Box>
                <Box
                  sx={{ cursor: 'pointer', display: 'flex', alignSelf: 'flex-start' }}
                  onClick={() => {
                    setProofOfInscription(null);
                    setNewDocToAdd(true);
                    clearErrors('proofOfInscriptionFile');
                  }}
                >
                  <CloseIcon fill={theme.palette.secondary.contrastText} />
                </Box>
              </Box>
            )}
          </RowContainer>
          <EmployeeModalInfoContainer sx={{ marginBottom: '16px' }}>
            <AccountWithMarginLeftBox>
              <Controller
                name="academicPeriodStart"
                control={control}
                render={({ field }) => (
                  <UPDatepicker
                    value={field.value}
                    onChange={value => {
                      field.onChange(value);
                      setDocToUpdate(true);
                      const { academicPeriodEnd } = getValues();
                      if (!isAcademicPeriodValid(value, academicPeriodEnd)) {
                        setError('periodInvalid', { type: t(EMPLOYEE__INVALID_ACADEMIC_PERIOD) });
                      } else {
                        clearErrors('periodInvalid');
                      }
                    }}
                    label={t(EMPLOYEE__ACADEMIC_YEAR_START)}
                    error={!!errors.periodInvalid}
                    helperText={getInputErrorText(errors, 'periodInvalid', undefined, 50)}
                    className="datepicker--white-background"
                  />
                )}
              />
            </AccountWithMarginLeftBox>
            <AccountWithMarginLeftBox>
              <Controller
                name="academicPeriodEnd"
                control={control}
                render={({ field }) => (
                  <UPDatepicker
                    value={field.value}
                    onChange={value => {
                      setDocToUpdate(true);
                      field.onChange(value);
                      const { academicPeriodStart } = getValues();
                      if (!isAcademicPeriodValid(academicPeriodStart, value)) {
                        setError('periodInvalid', { type: t(EMPLOYEE__INVALID_ACADEMIC_PERIOD) });
                      } else {
                        clearErrors('periodInvalid');
                      }
                    }}
                    label={t(EMPLOYEE__ACADEMIC_YEAR_END)}
                    error={!!errors.periodInvalid}
                    helperText={getInputErrorText(errors, 'periodInvalid', undefined, 50)}
                    className="datepicker--white-background"
                  />
                )}
              />
            </AccountWithMarginLeftBox>
          </EmployeeModalInfoContainer>
          {!!errors.periodInvalid && (
            <RowContainer sx={{ marginTop: '5px', marginLeft: '20px' }}>
              <ErrorText>{t(errors.periodInvalid.type)}</ErrorText>
            </RowContainer>
          )}
        </>
      )}
      {canWriteMfaOverride && (
        // gap, not per-item margins: SubsectionInputContainer's own gap is horizontal, and it drops
        // to 0 below 722px, so nothing else spaces these rows apart.
        <SecuritySubsectionContainer>
          <SubsectionTitleContainer>
            <SubsectionTitle>
              <strong>{t(GENERAL__SECURITY)}</strong>
            </SubsectionTitle>
          </SubsectionTitleContainer>
          <SubsectionInputContainer>
            <EmployeeInfoSmallBox sx={{ width: '50%' }}>
              <Controller
                name="mfaRequiredOverride"
                control={control}
                render={({ field }) => (
                  <UPMfaOverrideDropdown
                    value={field.value}
                    onChange={field.onChange}
                    // Clearing an override needs the linked User id, which only the employee read
                    // model can supply - the employee endpoints work in employee ids, which
                    // DELETE /UserMfa/{userId}/Override does not accept. Without it the value can
                    // still be set, so the option is dropped rather than offered and left broken.
                    hideNoOverrideOption={!employee?.userId && shouldSendMfaOverride(employee?.mfaRequiredOverride)}
                  />
                )}
              />
            </EmployeeInfoSmallBox>
          </SubsectionInputContainer>
          {!!employee?.userId && (
            <SubsectionInputContainer>
              <EmployeeInfoSmallBox sx={{ width: '50%' }}>
                <UPMfaResetAction userId={employee.userId} />
              </EmployeeInfoSmallBox>
            </SubsectionInputContainer>
          )}
        </SecuritySubsectionContainer>
      )}
      {canSeeEligibilityAndTerminationDetails && (
        <EligibilityAndTerminationDetails
          companyId={companyId}
          control={control}
          errors={errors}
          watch={watch}
          getValues={getValues}
        />
      )}
    </Box>
  );
};

export default AccountTab;
